import React, { useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { AnimatePresence, motion, PanInfo } from 'framer-motion';
import { Dialog, DialogPortal, DialogOverlay, DialogTitle, DialogDescription } from '../tailwind-component/ui/dialog';
import { cn } from '../tailwind-component/ui/utils';
import { WhatsNewConfig } from '../../core/interface/whats-new.interface';
import FeatureSlide from './FeatureSlide';
import ProgressIndicator from './ProgressIndicator';

interface WhatsNewModalProps {
    config: WhatsNewConfig;
    /** Skip / close (X, ESC, backdrop, or the Skip button) — never navigates. */
    onClose: () => void;
    /** Primary CTA pressed on the final slide — the caller decides where (if anywhere) to navigate. */
    onPrimaryAction: () => void;
    /** When `requiresSubscription` is true, swaps the default "Upgrade Now" label for "Explore" — the influencer already has this feature unlocked on their plan. */
    alreadySubscribed?: boolean;
}

const SWIPE_THRESHOLD = 60;

const slideVariants = {
    enter: (direction: number) => ({ x: direction > 0 ? 24 : -24, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (direction: number) => ({ x: direction < 0 ? 24 : -24, opacity: 0 }),
};

/**
 * Feature-announcement modal — built directly on Radix's Dialog primitives
 * (free focus-trap/ESC/ARIA) with plain flexbox centering (deliberately
 * avoiding the shared DialogContent's percentage-translate centering, which
 * this app's other dialogs work around with a `style={{transform:'inherit'}}`
 * escape hatch — flex is simpler and doesn't need it) plus a framer-motion
 * driven, swipeable, direction-aware slide carousel. Desktop: centered card.
 * Mobile: bottom sheet, full width with rounded top corners only.
 */
const WhatsNewModal: React.FC<WhatsNewModalProps> = ({ config, onClose, onPrimaryAction, alreadySubscribed }) => {
    const slides = config.slides;
    // Image-poster slides are self-contained artwork with no long-form text —
    // unlike the icon/title/description layout, they don't need a full-width
    // mobile bottom sheet; shrink-wrapping to the image avoids dead space on
    // either side and below it.
    const isImagePoster = slides.every((slide) => !!slide.image);
    const [[currentIndex, direction], setSlide] = useState<[number, number]>([0, 0]);
    const isLastSlide = currentIndex === slides.length - 1;
    const ctaLabel =
        config.ctaLabel ??
        (config.requiresSubscription ? (alreadySubscribed ? 'Explore' : 'Upgrade Now') : 'Try it Now');

    const goTo = (index: number) => {
        if (index < 0 || index >= slides.length || index === currentIndex) return;
        setSlide([index, index > currentIndex ? 1 : -1]);
    };

    const handleNext = () => {
        if (isLastSlide) {
            onPrimaryAction();
        } else {
            goTo(currentIndex + 1);
        }
    };

    const handleDragEnd = (_: unknown, info: PanInfo) => {
        if (info.offset.x < -SWIPE_THRESHOLD) handleNext();
        else if (info.offset.x > SWIPE_THRESHOLD) goTo(currentIndex - 1);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'ArrowRight') goTo(currentIndex + 1);
        if (e.key === 'ArrowLeft') goTo(currentIndex - 1);
    };

    return (
        <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
            <DialogPortal>
                {/* This app has several unrelated floating elements (mobile filter FABs,
                    drawers, tooltips) using z-index up to 9999 — sit comfortably above all
                    of them so nothing from the page underneath bleeds through the backdrop. */}
                <DialogOverlay className='z-[10000]' />
                <DialogPrimitive.Content
                    onKeyDown={handleKeyDown}
                    className={cn(
                        'group fixed inset-0 z-[10000] flex justify-center outline-none sm:items-center sm:p-6',
                        isImagePoster ? 'items-center p-4' : 'items-end'
                    )}
                >
                    <div
                        className={cn(
                            'relative flex max-h-[92vh] flex-col overflow-hidden bg-white',
                            'group-data-[state=open]:animate-in group-data-[state=closed]:animate-out group-data-[state=closed]:fade-out-0 group-data-[state=open]:fade-in-0 group-data-[state=closed]:zoom-out-95 group-data-[state=open]:zoom-in-95 duration-200',
                            isImagePoster
                                ? 'w-[84vw] max-w-[320px] rounded-2xl border border-gray-200 shadow-xl'
                                : 'w-full rounded-t-2xl border-t border-gray-200 pb-[env(safe-area-inset-bottom)]',
                            'sm:max-h-[85vh] sm:w-[680px] sm:max-w-[92vw] sm:rounded-2xl sm:border sm:shadow-xl'
                        )}
                    >
                        <DialogTitle className='sr-only'>{config.title}</DialogTitle>
                        <DialogDescription className='sr-only'>
                            A walkthrough announcing {config.title}
                        </DialogDescription>

                        <DialogPrimitive.Close className='absolute right-3 top-3 z-10 rounded-full bg-white/90 p-1.5 text-gray-500 opacity-90 shadow-sm backdrop-blur-sm transition hover:bg-gray-100 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-gray-200'>
                            <X size={18} />
                            <span className='sr-only'>Close</span>
                        </DialogPrimitive.Close>

                        <div className='relative min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain' style={{ touchAction: 'pan-y' }}>
                            <AnimatePresence mode='wait' custom={direction} initial={false}>
                                <motion.div
                                    key={slides[currentIndex].id}
                                    custom={direction}
                                    variants={slideVariants}
                                    initial='enter'
                                    animate='center'
                                    exit='exit'
                                    transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                                    drag='x'
                                    dragConstraints={{ left: 0, right: 0 }}
                                    dragElastic={0.2}
                                    onDragEnd={handleDragEnd}
                                >
                                    <FeatureSlide slide={slides[currentIndex]} />
                                </motion.div>
                            </AnimatePresence>
                        </div>

                        <div className='flex shrink-0 flex-col gap-3 border-t border-gray-100 px-6 py-4 sm:px-8'>
                            {/* Own row on mobile — squeezing "Slide X of Y" between the Skip
                                spacer and the CTA button on a narrow screen wraps it ugly. */}
                            <div className='flex justify-center sm:hidden'>
                                <ProgressIndicator current={currentIndex} total={slides.length} onDotClick={goTo} />
                            </div>

                            <div className='flex items-center justify-between gap-3'>
                                <div className='w-14 shrink-0'>
                                    {!isLastSlide && (
                                        <button
                                            type='button'
                                            onClick={onClose}
                                            className='text-sm font-medium text-gray-400 transition-colors hover:text-gray-600'
                                        >
                                            Skip
                                        </button>
                                    )}
                                </div>

                                <div className='hidden sm:block'>
                                    <ProgressIndicator current={currentIndex} total={slides.length} onDotClick={goTo} />
                                </div>

                                <motion.button
                                    type='button'
                                    whileTap={{ scale: 0.97 }}
                                    onClick={handleNext}
                                    className='shrink-0 rounded-lg bg-[#00aae5] px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#36a1d1]'
                                >
                                    {isLastSlide ? ctaLabel : 'Next'}
                                </motion.button>
                            </div>
                        </div>
                    </div>
                </DialogPrimitive.Content>
            </DialogPortal>
        </Dialog>
    );
};

export default WhatsNewModal;
